import { useState } from "react";
import axios from "axios";
import { Check, Waves, Sun, Sparkles, Cpu } from "lucide-react";
import { IMAGES } from "../../content";
import { FadeIn, ChapterLabel } from "./Chrome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Frame = ({ src, alt, className = "", testId }) => (
  <div className={`overflow-hidden border border-white/10 ${className}`} data-testid={testId}>
    <img src={src} alt={alt} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.5s] ease-out" />
  </div>
);

export const LocationSection = ({ t }) => (
  <section id="location" className="min-h-screen flex items-center py-28 px-6 md:px-12" data-testid="section-location">
    <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
      <div>
        <ChapterLabel no={t.location.no} label={t.location.label} />
        <FadeIn delay={0.1}>
          <h2 className="font-serif-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight whitespace-pre-line text-[#f2f0e9]">{t.location.title}</h2>
        </FadeIn>
        <FadeIn delay={0.2}>
          <p className="mt-8 text-base md:text-lg text-[#a1a3a1] leading-relaxed max-w-lg">{t.location.body}</p>
        </FadeIn>
        <div className="mt-12 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
          {t.location.stats.map((s, i) => (
            <FadeIn key={i} delay={0.1 * i} className="bg-[#0a0c0b] p-6">
              <div className="font-serif-display text-2xl md:text-3xl text-[#d4af37]">{s.v}</div>
              <div className="mt-2 text-xs md:text-sm text-[#8c877d]">{s.l}</div>
            </FadeIn>
          ))}
        </div>
      </div>
      <FadeIn delay={0.2} className="lg:pl-6">
        <Frame src={IMAGES.golf} alt="Is Molas golf course" className="aspect-[4/3]" testId="location-image" />
      </FadeIn>
    </div>
  </section>
);

export const ProjectSection = ({ t }) => (
  <section id="project" className="min-h-screen flex items-center py-28 px-6 md:px-12 bg-[#0e100f]" data-testid="section-project">
    <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
      <FadeIn className="order-2 lg:order-1">
        <Frame src={IMAGES.night} alt="Villa by night" className="aspect-[4/3]" testId="project-image" />
      </FadeIn>
      <div className="order-1 lg:order-2">
        <ChapterLabel no={t.project.no} label={t.project.label} />
        <FadeIn delay={0.1}>
          <h2 className="font-serif-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight text-[#f2f0e9]">{t.project.title}</h2>
        </FadeIn>
        <FadeIn delay={0.2}>
          <p className="mt-8 text-base md:text-lg text-[#a1a3a1] leading-relaxed max-w-lg">{t.project.body}</p>
        </FadeIn>
        <div className="mt-10 space-y-4">
          {t.project.points.map((p, i) => (
            <FadeIn key={i} delay={0.1 * i} className="flex items-center gap-4">
              <Check size={16} className="text-[#d4af37] shrink-0" />
              <span className="text-sm md:text-base text-[#f2f0e9]/85">{p}</span>
            </FadeIn>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const lifestyleIcons = [Waves, Sun, Sparkles, Cpu];

export const LifestyleSection = ({ t }) => (
  <section id="lifestyle" className="min-h-screen py-28 px-6 md:px-12" data-testid="section-lifestyle">
    <div className="max-w-7xl mx-auto w-full">
      <ChapterLabel no={t.lifestyle.no} label={t.lifestyle.label} />
      <div className="grid lg:grid-cols-2 gap-10 items-end mb-14">
        <FadeIn delay={0.1}>
          <h2 className="font-serif-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight text-[#f2f0e9]">{t.lifestyle.title}</h2>
        </FadeIn>
        <FadeIn delay={0.2}>
          <p className="text-base md:text-lg text-[#a1a3a1] leading-relaxed max-w-lg lg:justify-self-end">{t.lifestyle.body}</p>
        </FadeIn>
      </div>
      <div className="grid md:grid-cols-12 gap-4">
        <FadeIn className="md:col-span-7">
          <Frame src={IMAGES.pool} alt="Infinity pool at sunset" className="aspect-[16/10] md:h-full" testId="lifestyle-image-pool" />
        </FadeIn>
        <FadeIn delay={0.15} className="md:col-span-5">
          <Frame src={IMAGES.living} alt="Five-star interiors" className="aspect-[16/10] md:h-full" testId="lifestyle-image-living" />
        </FadeIn>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {t.lifestyle.items.map((it, i) => {
          const Icon = lifestyleIcons[i];
          return (
            <FadeIn key={i} delay={0.08 * i} className="bg-[#0a0c0b] p-7">
              <Icon size={20} className="text-[#d4af37]" />
              <div className="mt-4 font-serif-display text-xl text-[#f2f0e9]">{it.t}</div>
              <div className="mt-1.5 text-sm text-[#8c877d]">{it.d}</div>
            </FadeIn>
          );
        })}
      </div>
    </div>
  </section>
);

export const VillaSection = ({ t }) => (
  <section id="villa" className="min-h-screen flex items-center py-28 px-6 md:px-12 bg-[#0e100f]" data-testid="section-villa">
    <div className="max-w-7xl mx-auto w-full">
      <ChapterLabel no={t.villa.no} label={t.villa.label} />
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <FadeIn delay={0.1}>
            <h2 className="font-serif-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight whitespace-pre-line text-[#f2f0e9]">{t.villa.title}</h2>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-6 text-base md:text-lg text-[#a1a3a1]">{t.villa.body}</p>
          </FadeIn>
          <FadeIn delay={0.25} className="mt-10 hidden lg:block">
            <Frame src={IMAGES.terrace} alt="Villa terrace" className="aspect-[4/3]" testId="villa-image" />
          </FadeIn>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10 self-start">
          {t.villa.specs.map((s, i) => (
            <FadeIn key={i} delay={0.07 * i} className="bg-[#0e100f] p-6 md:p-8 min-h-[150px] flex flex-col justify-between" >
              <div>
                <span className="font-serif-display text-2xl md:text-4xl text-[#f2f0e9]">{s.v}</span>
                <span className="text-sm text-[#d4af37] ml-1.5">{s.u}</span>
              </div>
              <div className="text-xs md:text-sm tracking-[0.15em] uppercase text-[#8c877d] mt-4">{s.l}</div>
            </FadeIn>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const CustomSection = ({ t }) => (
  <section id="custom" className="min-h-screen flex items-center py-28 px-6 md:px-12" data-testid="section-custom">
    <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
      <div>
        <ChapterLabel no={t.custom.no} label={t.custom.label} />
        <FadeIn delay={0.1}>
          <h2 className="font-serif-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight text-[#f2f0e9]">{t.custom.title}</h2>
        </FadeIn>
        <FadeIn delay={0.2}>
          <p className="mt-8 text-base md:text-lg text-[#a1a3a1] leading-relaxed max-w-lg">{t.custom.body}</p>
        </FadeIn>
        <div className="mt-10 space-y-4">
          {t.custom.items.map((p, i) => (
            <FadeIn key={i} delay={0.1 * i} className="flex items-center gap-4">
              <span className="font-serif-display italic text-[#d4af37] text-lg w-6">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm md:text-base text-[#f2f0e9]/85">{p}</span>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.3} className="mt-10 border-l-2 border-[#d4af37] pl-5">
          <p className="font-serif-display italic text-lg text-[#f2f0e9]/70">{t.custom.note}</p>
        </FadeIn>
      </div>
      <FadeIn delay={0.2} className="grid gap-4">
        <Frame src={IMAGES.bedroom} alt="Customizable bedroom" className="aspect-[16/10]" testId="custom-image-bedroom" />
        <Frame src={IMAGES.living} alt="Customizable living" className="aspect-[16/10] hidden md:block" testId="custom-image-living" />
      </FadeIn>
    </div>
  </section>
);

export const MasterplanSection = ({ t }) => (
  <section id="masterplan" className="relative min-h-screen flex items-end py-28 overflow-hidden" data-testid="section-masterplan">
    <div className="absolute inset-0">
      <img src={IMAGES.aerial} alt="Masterplan aerial" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0c0b] via-[#0a0c0b]/45 to-[#0a0c0b]/25" />
    </div>
    <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12">
      <ChapterLabel no={t.masterplan.no} label={t.masterplan.label} />
      <FadeIn delay={0.1}>
        <h2 className="font-serif-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight max-w-2xl text-[#f2f0e9]">{t.masterplan.title}</h2>
      </FadeIn>
      <FadeIn delay={0.2}>
        <p className="mt-6 text-base md:text-lg text-[#f2f0e9]/75 leading-relaxed max-w-xl">{t.masterplan.body}</p>
      </FadeIn>
      <div className="mt-12 flex flex-wrap gap-px bg-white/15 border border-white/15 w-fit backdrop-blur-md">
        {t.masterplan.stats.map((s, i) => (
          <FadeIn key={i} delay={0.1 * i} className="bg-[#0a0c0b]/70 px-8 py-6">
            <div className="font-serif-display text-2xl md:text-3xl text-[#d4af37]">{s.v}</div>
            <div className="mt-1.5 text-xs md:text-sm text-[#a1a3a1]">{s.l}</div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

export const ContactSection = ({ t, lang }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, { ...form, lead_type: "buyer", language: lang });
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="min-h-screen flex items-center py-28 px-6 md:px-12 bg-[#0e100f]" data-testid="section-contact">
      <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-14 lg:gap-24">
        <div>
          <ChapterLabel no={t.contact.no} label={t.contact.label} />
          <FadeIn delay={0.1}>
            <h2 className="font-serif-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight text-[#f2f0e9]">{t.contact.title}</h2>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-8 text-base md:text-lg text-[#a1a3a1] leading-relaxed max-w-lg">{t.contact.body}</p>
          </FadeIn>
        </div>
        <FadeIn delay={0.2}>
          <form onSubmit={submit} className="space-y-4" data-testid="buyer-contact-form">
            <input required className="input-dark" placeholder={t.contact.name} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
            <input required type="email" className="input-dark" placeholder={t.contact.email} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
            <input className="input-dark" placeholder={t.contact.phone} value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
            <textarea rows={4} className="input-dark resize-none" placeholder={t.contact.message} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
            <button type="submit" disabled={status === "sending"} className="btn-gold w-full" data-testid="contact-submit-btn">
              {status === "sending" ? t.contact.sending : t.contact.submit}
            </button>
            {status === "success" && <p className="text-sm text-[#d4af37]" data-testid="contact-success-msg">{t.contact.success}</p>}
            {status === "error" && <p className="text-sm text-red-400" data-testid="contact-error-msg">{t.contact.error}</p>}
          </form>
        </FadeIn>
      </div>
    </section>
  );
};
